import { FormSkeleton, useNotify, UserPermission } from '@globaleris/ui.library'
import { Button, List, ListItem, makeStyles, Typography, useTheme } from '@material-ui/core'
import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useHistory } from 'react-router-dom'
import { EnumBloqueoCapacidad } from '../../../api'
import ButtonWithPermissions from '../../../components/buttonWithPermissions/buttonWithPermissions'
import Page, { PageLayout } from '../../../components/page/page'
import PageCenterColumn from '../../../components/page/pageCenterColumn'
import PageHeader from '../../../components/page/pageHeader'
import PageLeftColumn from '../../../components/page/pageLeftColumn'
import PageRightColumn from '../../../components/page/pageRightColumn'
import { hasPermission } from '../../../helpers/permissionsHelper'
import { useConfirmationDialog } from '../../../hooks/useConfirmationDialog'
import { useHttpError } from '../../../hooks/useHttpError'
import { NonAuthRoutes } from '../../../model'
import { ConfirmationDialogs } from '../../../model/enums/confirmationDialogs'
import { Resources } from '../../../model/RBAC/constants'
import { CapacityModel } from '../capacity.model'
import { getCapacityItem, updateLockState } from '../capacity.service'
import { Allocations } from '../forms/allocations'
import { AddendumsCard } from './addendum/addendumCard'
import { styles } from './editBundle.styles'

export const EditCapacityFormalizedPage: React.FC<any> = props => {
  let history = useHistory()
  const [t] = useTranslation(['capacity', 'enums'])
  const currentTheme = useTheme()
  const useStyles = makeStyles(styles(currentTheme))
  const classes = useStyles()
  const { info } = useNotify()

  const [capacity, setCapacity] = useState<CapacityModel | undefined>(undefined)
  const [canEditAllocations, setCanEditAllocations] = useState<boolean>(false)

  const { getConfirmation, closeConfirmation } = useConfirmationDialog()
  const { showError, clearError, errorProps } = useHttpError()

  useEffect(() => {
    const fetchCapacity = async (id: number) => {
      await getCapacityItem(id).then(response => {
        if (response.success) {
          clearError()
          setCapacity(response.data)
          setCanEditAllocations(response.data.lockState === EnumBloqueoCapacidad.Libre)
        } else {
          response.error &&
            showError({
              title: t('capacity:forms.editFormalized.title'),
              errorCode: response.error.code,
              // actions: [
              //   {
              //     label: t('capacity:forms.editFormalized.mainActions.back'),
              //     onClick: () => {
              //       history.push(NonAuthRoutes.capacity)
              //     },
              //     default: true
              //   }
              // ],
              onRefresh: () => fetchCapacity(id)
            })
        }
      })
    }

    if (props.location.state) {
      fetchCapacity(props.location.state.idCapacity)
    } else {
      history.push(NonAuthRoutes.capacity)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  //#region Handlers

  const onLockActionHandler = async () => {
    getConfirmation(ConfirmationDialogs.LOCK).then(async () => {
      await updateCapacityLockState(EnumBloqueoCapacidad.BloqueadoUsuario).then(success => {
        if (success) {
          capacity && setCapacity({ ...capacity, lockState: EnumBloqueoCapacidad.BloqueadoUsuario })
          setCanEditAllocations(false)
        }
        closeConfirmation()
      })
    })
  }

  const onUnlockActionHandler = async () => {
    getConfirmation(ConfirmationDialogs.UNLOCK).then(async () => {
      await updateCapacityLockState(EnumBloqueoCapacidad.Libre).then(success => {
        if (success) {
          capacity && setCapacity({ ...capacity, lockState: EnumBloqueoCapacidad.Libre })
          setCanEditAllocations(true)
        }
        closeConfirmation()
      })
    })
  }

  //#endregion

  const updateCapacityLockState = async (newState: EnumBloqueoCapacidad): Promise<boolean> => {
    if (capacity && capacity.id) {
      return await updateLockState(capacity.id, newState).then(success => {
        if (success) {
          info(
            newState === EnumBloqueoCapacidad.Libre
              ? t('capacity:forms.editFormalized.notifications.success.unlock')
              : t('capacity:forms.editFormalized.notifications.success.lock')
          )
        }
        return success
      })
    } else {
      return false
    }
  }

  return (
    <Page layout={PageLayout.MULTI_COLUMN} error={errorProps}>
      <PageLeftColumn>
        {capacity ? (
          <>
            <Typography variant="h1" color="primary" noWrap={true}>
              {capacity?.code}{' '}
            </Typography>

            <List className={classes.resume}>
              <ListItem disableGutters>
                <Typography variant={'h2'}>{capacity?.translatedService}</Typography>
              </ListItem>
              <ListItem disableGutters>
                <Typography variant={'body2'}>{capacity?.logisticElement?.description}</Typography>
              </ListItem>
              <ListItem disableGutters>
                <Typography variant={'body2'}>{capacity?.formattedCapacity} kWh/día</Typography>
              </ListItem>
              <ListItem divider disableGutters>
                <Typography variant={'body2'}>
                  {capacity?.formattedInitDate} - {capacity?.formattedEndDate}
                </Typography>
              </ListItem>
              <ListItem divider disableGutters>
                <Typography variant={'caption'}>
                  {t('capacity:forms.editFormalized.labels.duration')}
                </Typography>
                <Typography variant={'subtitle2'}>{capacity?.translatedDuration}</Typography>
              </ListItem>
              <ListItem divider disableGutters>
                <Typography variant={'caption'}>
                  {t('capacity:forms.editFormalized.labels.rawMaterial')}
                </Typography>
                <Typography variant={'subtitle2'}>{capacity?.formattedRawMaterial}</Typography>
              </ListItem>
              <ListItem divider disableGutters>
                <Typography variant={'caption'}>
                  {t('capacity:forms.editFormalized.labels.nature')}
                </Typography>
                <Typography variant={'subtitle2'}>{capacity?.translatedNature}</Typography>
              </ListItem>
              <ListItem divider disableGutters>
                <Typography variant={'caption'}>
                  {t('capacity:forms.editFormalized.labels.premium')}
                </Typography>
                <Typography variant={'subtitle2'}>{capacity?.formattedPremium} kWh/dia</Typography>
              </ListItem>
              <ListItem divider disableGutters>
                <Typography variant={'caption'}>
                  {t('capacity:forms.editFormalized.labels.signDate')}
                </Typography>
                <Typography variant={'subtitle2'}>{capacity?.formattedSignDate}</Typography>
              </ListItem>
            </List>
          </>
        ) : (
          <FormSkeleton withTitle={true} elevation={0} columns={1} inputs={9}></FormSkeleton>
        )}
      </PageLeftColumn>
      <PageCenterColumn>
        <PageHeader title={t('capacity:forms.editFormalized.title')}>
          {capacity && (
            <>
              {capacity?.lockState === EnumBloqueoCapacidad.Libre ? (
                <ButtonWithPermissions
                  className={classes.cardAction}
                  size="small"
                  color="primary"
                  onClick={onLockActionHandler}
                  hasPermission={hasPermission(Resources.Balance, [UserPermission.EDIT])}
                >
                  {t('capacity:forms.editFormalized.mainActions.lock')}
                </ButtonWithPermissions>
              ) : (
                <ButtonWithPermissions
                  className={classes.cardAction}
                  size="small"
                  color="primary"
                  onClick={onUnlockActionHandler}
                  hasPermission={hasPermission(Resources.Balance, [UserPermission.EDIT])}
                >
                  {t('capacity:forms.editFormalized.mainActions.unlock')}
                </ButtonWithPermissions>
              )}
            </>
          )}

          <Button
            className={classes.cardAction}
            variant="contained"
            size="small"
            color="primary"
            onClick={() => history.push(NonAuthRoutes.capacity)}
          >
            {t('capacity:forms.editFormalized.mainActions.back')}
          </Button>
        </PageHeader>
        <Allocations
          canSend={false}
          canEdit={canEditAllocations}
          data={capacity?.allocations}
        ></Allocations>
        <AddendumsCard addendums={capacity?.addendums} />
      </PageCenterColumn>
      <PageRightColumn></PageRightColumn>
    </Page>
  )
}
